import { getAuth } from "firebase/auth";
import {
  apiUrlDev,
  apiUrlDevFirebase,
  apiUrlLocal,
  apiUrlProdFirebase,
} from "../../globalVariables";
import { authDev, authProd } from "../../firebase";

/** True when the dashboard itself is being served from a dev machine. */
export const isLocalDashboard = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

/**
 * Pick the backend URLs for the chosen environment.
 *
 * - `prod` ALWAYS hits the production Cloud Function, even when this
 *   dashboard is running on localhost. Falling back to anything else would
 *   silently mix dev data into a "Prod" view.
 * - `dev` prefers the Firebase Functions emulator (port 5003, `addy-ai-dev`
 *   project, which is what you get when running the backend with
 *   `firebase emulators:start`), then any plain-Node local server
 *   (`apiUrlLocal`, port 8080), and finally the deployed dev Cloud
 *   Function. Outside localhost we only have the deployed dev function.
 * - `local` hits ONLY a backend running on this machine. It exists so a page
 *   whose routes are not deployed yet can still be developed against — which
 *   environment's data you get is whatever the local backend's own config
 *   points at, so it is deliberately labelled separately from prod and dev
 *   rather than being folded into either.
 */
const getBaseUrlsForEnv = (env) => {
  const isLocal = isLocalDashboard();

  if (env === "local") {
    // Prefer the nodemon webserver (8080) first — it hot-reloads new admin
    // routes. The Functions emulator (5003) is second because it often runs
    // stale code until restarted.
    return isLocal ? [apiUrlLocal, apiUrlDevFirebase] : [];
  }
  if (env === "dev") {
    // When developing on localhost, prefer the nodemon webserver (8080) first —
    // it's what `npm run dev:webserver` runs and hot-reloads new routes. Fall
    // back to the Functions emulator (5003) only if 8080 isn't up.
    return isLocal ? [apiUrlLocal, apiUrlDevFirebase, apiUrlDev] : [apiUrlDev];
  }
  return [apiUrlProdFirebase];
};

const getAuthenticatedAdmin = async (env) => {
  const auth = env === "prod" ? authProd : authDev;
  const user = auth?.currentUser || getAuth()?.currentUser;
  if (!user) {
    throw new Error("Not authenticated. Please refresh and sign in again.");
  }

  return {
    userId: user.uid,
    idToken: await user.getIdToken(),
  };
};

const buildUrl = (baseUrl, path, params = {}) => {
  // IMPORTANT: the `admin` Cloud Function name and the express mount path
  // inside `cloudFunctions/admin.js` are BOTH literally "/admin". The base
  // URLs in `globalVariables.js` only cover the first one (the Cloud
  // Function name), so every request still needs a second `/admin` prefix
  // before the route path — exactly how OrganizationLimits / GlobalSearch /
  // every other working admin-dashboard page builds their URLs.
  // e.g. baseUrl = "https://.../admin"  +  "/admin" + "/usage-analytics/overview"
  //   →  https://.../admin/admin/usage-analytics/overview
  // Cloud Function strips "admin", express strips "/admin", then
  // `routes/admin.js` matches "/usage-analytics/overview".
  const trimmedBase = baseUrl.replace(/\/$/, "");
  const url = new URL(`${trimmedBase}/admin${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
};

export const fetchWithFallback = async (
  path,
  params = {},
  { timeoutMs = 60000, env = "prod", method = "GET", body = undefined } = {},
) => {
  const { userId, idToken } = await getAuthenticatedAdmin(env);
  const baseUrls = getBaseUrlsForEnv(env);
  const attempts = [];

  for (const baseUrl of baseUrls) {
    const url = buildUrl(baseUrl, path, { ...params, userId });
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      // The user id remains for legacy admin routes, but referral-admin routes
      // bind it to this Firebase ID token on the server.
      const fetchOptions = {
        signal: controller.signal,
        method,
        headers: { Authorization: `Bearer ${idToken}` },
      };
      if (body !== undefined) {
        fetchOptions.headers["Content-Type"] = "application/json";
        fetchOptions.body = JSON.stringify(body);
      }
      const res = await fetch(url, fetchOptions);
      clearTimeout(timer);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        const reason = `${res.status} ${res.statusText || ""}`.trim();
        attempts.push({ baseUrl, status: reason, body: text.slice(0, 160) });
        // eslint-disable-next-line no-console
        console.warn(
          `[OrgUsageAnalytics] [${env}] ${baseUrl} → ${reason}`,
          text.slice(0, 200),
        );
        continue;
      }
      return await res.json();
    } catch (err) {
      const msg = err?.message || String(err);
      attempts.push({ baseUrl, status: "network/abort", body: msg });
      // eslint-disable-next-line no-console
      console.warn(`[OrgUsageAnalytics] [${env}] ${baseUrl} → ${msg}`);
    }
  }

  const summary = attempts
    .map((a) => `• ${a.baseUrl} → ${a.status}${a.body ? ` (${a.body})` : ""}`)
    .join("\n");
  const hint =
    env === "local"
      ? "\n\nNo backend is answering on this machine. Start one from backend/functions with `npm run dev:webserver` (port 8080) or `npm run serve` (emulator, port 5003)."
      : "";
  throw new Error(
    `All ${env} endpoints failed:\n${summary || "(no endpoints tried)"}${hint}`,
  );
};

/**
 * Same routing, auth and fallback as `fetchWithFallback`, for a binary answer
 * (an audio clip). Resolves to a Blob.
 */
export const fetchBlobWithFallback = async (
  path,
  params = {},
  { timeoutMs = 60000, env = "prod" } = {},
) => {
  const { userId, idToken } = await getAuthenticatedAdmin(env);
  const baseUrls = getBaseUrlsForEnv(env);
  const attempts = [];

  for (const baseUrl of baseUrls) {
    const url = buildUrl(baseUrl, path, { ...params, userId });
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(url, {
        signal: controller.signal,
        headers: { Authorization: `Bearer ${idToken}` },
      });
      clearTimeout(timer);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        attempts.push({ baseUrl, status: `${res.status}`, body: text.slice(0, 160) });
        continue;
      }
      return await res.blob();
    } catch (err) {
      attempts.push({ baseUrl, status: "network/abort", body: err?.message || String(err) });
    }
  }

  const summary = attempts
    .map((a) => `• ${a.baseUrl} → ${a.status}${a.body ? ` (${a.body})` : ""}`)
    .join("\n");
  throw new Error(`All ${env} endpoints failed:\n${summary || "(no endpoints tried)"}`);
};

export const dateRangeParams = (days) => {
  const endDate = Date.now();
  const startDate = endDate - days * 24 * 60 * 60 * 1000;
  return { startDate: String(startDate), endDate: String(endDate) };
};

export const fetchUsageOverview = (params, env) =>
  fetchWithFallback("/usage-analytics/overview", params, { env });

export const fetchOrgSummary = (organizationId, params, env) =>
  fetchWithFallback(`/usage-analytics/${organizationId}/summary`, params, {
    env,
  });

export const fetchOrgMembers = (organizationId, params, env) =>
  fetchWithFallback(`/usage-analytics/${organizationId}/members`, params, {
    env,
  });

export const fetchOrgUsageOverTime = (organizationId, params, env) =>
  fetchWithFallback(
    `/usage-analytics/${organizationId}/usage-over-time`,
    params,
    { env },
  );

export const fetchOrgUsersByActivity = (organizationId, params, env) =>
  fetchWithFallback(
    `/usage-analytics/${organizationId}/users-by-activity`,
    params,
    { env },
  );

export const fetchOrgFeatureBreakdown = (organizationId, params, env) =>
  fetchWithFallback(
    `/usage-analytics/${organizationId}/feature-breakdown`,
    params,
    { env },
  );

export const fetchOrgRecentLoanAccess = (organizationId, params, env) =>
  fetchWithFallback(
    `/usage-analytics/${organizationId}/recent-loan-access`,
    params,
    { env },
  );

export const fetchUserProfile = (organizationId, userId, params, env) =>
  fetchWithFallback(
    `/usage-analytics/${organizationId}/users/${userId}`,
    params,
    { env },
  );

/**
 * Combined dashboard fetch. Replaces six separate requests
 * (summary, members, usage-over-time, feature-breakdown, users-by-activity,
 * recent-loan-access) with one. Backend shares Firestore reads across all
 * aggregations so the org detail page loads ~3-4x faster.
 *
 * Response shape:
 * {
 *   summary: { ... },
 *   members: { members: [], partial },
 *   usageOverTime: { points: [], partial },
 *   featureBreakdown: { features: [], partial },
 *   usersByActivity: { buckets: [], partial },
 *   recentLoanAccess: { entries: [], partial },
 *   partial: boolean,
 * }
 */
export const fetchOrgDashboard = (organizationId, params, env) =>
  fetchWithFallback(
    `/usage-analytics/${organizationId}/dashboard`,
    params,
    { env },
  );
